import React, { useMemo, useState } from 'react';
import { BarChart3Icon, MenuIcon } from 'lucide-react';
import { DepartmentAnalysis } from '../components/reporting/DepartmentAnalysis';
import { RevenueByOwnerTable } from '../components/reporting/RevenueByOwnerTable';
import { TrendChart } from '../components/reporting/TrendChart';
import { type DepartmentAnalysisClassification, type DepartmentAnalysisJob, type DepartmentBreakdownKey, type ReportMonth, type TrendMetricKey } from '../components/reporting/reportingTypes';
import { Sidebar } from '../components/Sidebar';
import { useSettingsStore } from '../data/settingsStore';
import { INITIAL_JOBS } from './Jobs';
import { useScreenInit } from '../useScreenInit';
type ReportingInvoice = {
  value: number;
  date: string;
  paymentStatus: 'paid' | 'unpaid' | 'cancelled';
};
type ReportingJob = DepartmentAnalysisJob & {
  startDate: string;
  endDate?: string;
  dateCreated: string;
  projectManager: string;
  projectDirector: string;
  invoices?: ReportingInvoice[];
};
type ReportingClassification = DepartmentAnalysisClassification & {
  likelihood: number;
};
type ReportingTab = 'overview' | 'departments' | 'revenue';
const REPORTING_CLASSIFICATIONS: Record<string, ReportingClassification> = {
  '1': {
    likelihood: 0.9,
    source: 'Existing client',
    sector: 'Commercial'
  },
  '2': {
    likelihood: 0.8,
    source: 'Repeat business',
    sector: 'Residential'
  },
  '3': {
    likelihood: 0.75,
    source: 'Professional network',
    sector: 'Commercial'
  },
  '4': {
    likelihood: 0.6,
    source: 'Website enquiry',
    sector: 'Mixed-use'
  },
  '5': {
    likelihood: 0.85,
    source: 'Existing client',
    sector: 'Retail'
  },
  '6': {
    likelihood: 0.7,
    source: 'Word of mouth',
    sector: 'Residential'
  },
  '7': {
    likelihood: 1,
    source: 'Repeat business',
    sector: 'Commercial'
  },
  '8': {
    likelihood: 1,
    source: 'Professional network',
    sector: 'Residential'
  },
  '9': {
    likelihood: 0.35,
    source: 'Website enquiry',
    sector: 'Residential'
  },
  '10': {
    likelihood: 0.55,
    source: 'Email / newsletter',
    sector: 'Commercial'
  }
};
const DEFAULT_TREND_METRICS: TrendMetricKey[] = ['ongoing', 'quoted', 'invoicing', 'targets'];
const REPORTING_JOBS = INITIAL_JOBS as unknown as ReportingJob[];
export function Reporting() {
  useScreenInit();
  const {
    departments,
    departmentBudgetAllocations
  } = useSettingsStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ReportingTab>('overview');
  const [departmentBreakdown, setDepartmentBreakdown] = useState<DepartmentBreakdownKey>('status');
  const [selectedTrendMetrics, setSelectedTrendMetrics] = useState<TrendMetricKey[]>(DEFAULT_TREND_METRICS);
  const months = useMemo(() => createReportingMonths(departmentBudgetAllocations), [departmentBudgetAllocations]);
  const activeDepartments = useMemo(() => departments.filter(department => department.status === 'active').map(({
    id,
    name
  }) => ({
    id,
    name
  })), [departments]);
  const toggleTrendMetric = (metric: TrendMetricKey) => {
    setSelectedTrendMetrics(current => current.includes(metric) ? current.filter(item => item !== metric) : [...current, metric]);
  };
  return <div className="flex h-screen w-screen overflow-hidden bg-[#0F3D5C] font-sans" data-id="element-1985" data-mp="310ui9049g7qs">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-1986" data-mp="35znktxi0eec6" />
      <main className="flex min-w-0 flex-1 overflow-hidden p-0 md:py-3 md:pr-3" data-id="element-1987" data-mp="35xkwrj0f2va3">
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-slate-50 md:rounded-2xl md:shadow-xl" data-id="element-1988" data-mp="3ax5x9xv8jbox">
          <header className="border-b border-slate-200 bg-white px-5 py-5 md:px-8 md:py-7" data-id="element-1989" data-mp="3fqrzsx7opz70">
            <button type="button" onClick={() => setSidebarOpen(true)} className="mb-4 flex items-center gap-2 text-[#14466B] focus:outline-none focus:ring-2 focus:ring-blue-200 md:hidden" aria-label="Open menu" data-id="element-1990" data-mp="2i9t8ri5iktsz">
              <MenuIcon className="h-6 w-6" data-id="element-1991" data-mp="osnimb95hpmx" />
            </button>
            <div className="flex items-start gap-3" data-id="element-1992" data-mp="12jaiw7uk7nl4">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-1993" data-mp="12k24cpug04xo">
                <BarChart3Icon className="h-5 w-5" data-id="element-1994" data-mp="1cgwzqzvf5p7q" />
              </span>
              <div data-id="element-1995" data-mp="1cj1ekcuuw8ms">
                <h1 className="text-2xl font-bold text-slate-900" data-id="element-1996" data-mp="17hu7yh4g9fhp">Reporting</h1>
                <p className="mt-1 text-sm text-slate-500" data-id="element-1997" data-mp="17jy6sknhrob4">
                  A high-level view of business performance and project
                  pipeline.
                </p>
              </div>
            </div>
          </header>

          <div className="border-b border-slate-200 bg-white px-5 md:px-8" data-id="element-1998" data-mp="3n8gduym1yax5">
            <nav className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Reporting views" data-id="element-1999" data-mp="3nat2fumbfjj6">
              <ReportingTabButton id="reporting-overview-tab" panelId="reporting-overview-panel" active={activeTab === 'overview'} label="Business overview" onClick={() => setActiveTab('overview')} data-id="element-2000" data-mp="1xt5o8tzspg3t" />
              <ReportingTabButton id="reporting-departments-tab" panelId="reporting-departments-panel" active={activeTab === 'departments'} label="Department analysis" onClick={() => setActiveTab('departments')} data-id="element-2001" data-mp="1szte1ovv5bul" />
              <ReportingTabButton id="reporting-revenue-tab" panelId="reporting-revenue-panel" active={activeTab === 'revenue'} label="Revenue by owner" onClick={() => setActiveTab('revenue')} data-id="element-2002" data-mp="1629bruhnddh2" />
            </nav>
          </div>

          <div className="px-5 py-6 md:px-8 md:py-8" data-id="element-2003" data-mp="34e6wge7ukz38">
            <section id="reporting-overview-panel" role="tabpanel" aria-labelledby="reporting-overview-tab" hidden={activeTab !== 'overview'} data-id="element-2004" data-mp="h83quusyf87p">
              <TrendChart data={months} selectedMetrics={selectedTrendMetrics} onToggleMetric={toggleTrendMetric} data-id="element-2005" data-mp="87yl0p3eshvn" />
            </section>
            <section id="reporting-departments-panel" role="tabpanel" aria-labelledby="reporting-departments-tab" hidden={activeTab !== 'departments'} data-id="element-2006" data-mp="zx1r8aeuw2ax">
              {activeTab === 'departments' && <DepartmentAnalysis breakdownKey={departmentBreakdown} departments={activeDepartments} jobs={REPORTING_JOBS} classifications={REPORTING_CLASSIFICATIONS} onBreakdownKeyChange={setDepartmentBreakdown} data-id="element-2007" data-mp="m7gg1m7asrdg" />}
            </section>
            <section id="reporting-revenue-panel" role="tabpanel" aria-labelledby="reporting-revenue-tab" hidden={activeTab !== 'revenue'} data-id="element-2008" data-mp="1m8v2l0ys02fu">
              {activeTab === 'revenue' && <RevenueByOwnerTable jobs={REPORTING_JOBS} months={months} data-id="element-2009" data-mp="2071fet8h31n0" />}
            </section>
          </div>
        </div>
      </main>
    </div>;
}
function ReportingTabButton({
  id,
  panelId,
  active,
  label,
  onClick
}: {
  id: string;
  panelId: string;
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" id={id} role="tab" aria-selected={active} aria-controls={panelId} onClick={onClick} className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-200 ${active ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2010" data-mp="2evxx7i0bzt0t">
      {label}
      {active && <span className="absolute inset-x-4 bottom-0 h-0.5 bg-[#14466B]" data-id="element-2011" data-mp="3e88hkklboiiw" />}
    </button>;
}
function createReportingMonths(departmentBudgetAllocations: Record<string, Record<string, number>>): ReportMonth[] {
  const today = new Date();
  return Array.from({
    length: 8
  }, (_, index) => {
    const date = new Date(today.getFullYear(), today.getMonth() - 5 + index, 1);
    const key = monthKey(date);
    const monthStart = `${key}-01`;
    const monthEnd = endOfMonth(date);
    const targets = Object.values(departmentBudgetAllocations).reduce((sum, allocations) => sum + (allocations[key] || 0), 0);
    const metrics = {
      ongoing: REPORTING_JOBS.filter(job => job.status === 'ongoing' && job.startDate <= monthEnd && (!job.endDate || job.endDate >= monthStart)).reduce((sum, job) => sum + job.value, 0),
      closed: REPORTING_JOBS.filter(job => job.status === 'closed' && sameMonth(job.endDate || job.dateCreated, key)).reduce((sum, job) => sum + job.value, 0),
      quoted: REPORTING_JOBS.filter(job => job.status === 'quote' && sameMonth(job.dateCreated, key)).reduce((sum, job) => sum + job.value, 0),
      quotedWeighted: REPORTING_JOBS.filter(job => (job.status === 'quote' || job.status === 'pcc_in_progress') && sameMonth(job.dateCreated, key)).reduce((sum, job) => sum + job.value * classificationFor(job.id).likelihood, 0),
      invoicing: REPORTING_JOBS.flatMap(job => job.invoices || []).filter(invoice => invoice.paymentStatus !== 'cancelled' && sameMonth(invoice.date, key)).reduce((sum, invoice) => sum + invoice.value, 0),
      targets
    };
    return {
      key,
      label: date.toLocaleDateString('en-GB', {
        month: 'short',
        year: '2-digit'
      }),
      metrics
    };
  });
}
function classificationFor(jobId: string): ReportingClassification {
  return REPORTING_CLASSIFICATIONS[jobId] || {
    likelihood: 0.5,
    source: 'Unclassified source',
    sector: 'Unclassified sector'
  };
}
function monthKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
function endOfMonth(date: Date) {
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-${String(nextMonth.getDate()).padStart(2, '0')}`;
}
function sameMonth(dateValue: string, key: string) {
  return dateValue.slice(0, 7) === key;
}